"use client";

import posthog from "posthog-js";
import { AttributedLink } from "@/components/marketing/attributed-link";

/**
 * The in-content ask on a guide page.
 *
 * Guides are ~60% of organic landing traffic and, measured on 2026-08-24,
 * produced 2 signups and 0 customers in 90 days. The cause was structural: on
 * DESKTOP the only account link sits at ~96% page depth, below every internal
 * exit, and the one in-content form was <GuideLeadCapture> — an email-for-a-PDF
 * box that persisted no lead and had captured 3 addresses in the product's
 * lifetime. (On mobile a permanent /signup bar already exists via
 * <StickyMobileCta>, mounted in app/(marketing)/layout.tsx and hidden on md+;
 * it has not moved the number either, which is why an in-content ask inside the
 * reading flow is worth having on both.)
 *
 * PLACEMENT is the whole design, and it is deliberately ABOVE the table of
 * contents, not below it:
 *
 *   - <TableOfContents> is `sticky top-16 z-10` with an opaque `bg-gray-50`
 *     (components/marketing/table-of-contents.tsx:22). Anything placed after it
 *     scrolls UNDERNEATH it within about one card-height.
 *   - A TOC is a routing device. A reader who scans it and jumps to a section
 *     skips everything between the TOC and that heading — which is exactly the
 *     high-intent reader this ask is for.
 *
 * Above the TOC it is occlusion-proof and cannot be routed around.
 *
 * DESTINATION is /signup, copying the mechanic from the highest-converting page
 * on the site: /features/rams turns ~26% of its visitors into signups with this
 * exact shape — one button, one hop, no form. The copy promises what /signup
 * actually delivers (a free account whose Starter tier covers one real job), NOT
 * a no-account artifact; /start-project is the flow that hands over documents
 * without an account, and promising that here while linking to a credentials
 * form would be a bait-and-switch.
 *
 * EVENT NAME is deliberately NOT `landing_cta_clicked`. That name is owned by
 * <FunnelCtaLink>, which attaches `$feature/funnel-v2` to every emission
 * (components/funnel/funnel-cta-link.tsx:53-60). Emitting it from here — with no
 * variant, from 56 pages that are not in the experiment — would contaminate the
 * funnel-v2 exposure/conversion split with unassigned traffic. `guide_cta_clicked`
 * keeps the two answerable separately.
 */
export function GuideStartCta({
  slug,
  headline = "Create your first job pack free",
  body = "Set up a free account and generate the RAMS, construction phase plan and site induction for one real job. No card needed.",
}: {
  /** Guide slug, so a signup can be traced to the article that produced it. */
  slug: string;
  headline?: string;
  body?: string;
}) {
  return (
    <div className="border-t border-gray-100 bg-white px-4 py-10 sm:px-6">
      <div className="mx-auto max-w-3xl rounded-xl border border-accent/30 bg-accent-light p-6 sm:p-8">
        <p className="font-heading text-xl font-extrabold tracking-tight text-navy sm:text-2xl">
          {headline}
        </p>
        <p className="mt-2 text-sm leading-relaxed text-muted sm:text-base">
          {body}
        </p>
        <AttributedLink
          href="/signup"
          className="mt-5 inline-flex min-h-[48px] items-center justify-center rounded-[10px] bg-accent px-7 py-3 text-[15px] font-bold text-white transition-colors hover:bg-accent-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-dark"
          onClick={() => {
            if (!posthog.__loaded) return;
            posthog.capture("guide_cta_clicked", {
              cta_location: `guide:${slug}`,
              destination: "/signup",
              entry_path:
                typeof window === "undefined" ? null : window.location.pathname,
            });
          }}
        >
          Start free &rarr;
        </AttributedLink>
      </div>
    </div>
  );
}
